import {
  W9BusinessTaxClassifications,
  W9FormData,
  W9LLCTaxClassifications,
} from '@bybeam/platform-types';
import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import { fieldUpdate } from '@platform-ui-common/hooks/useForm/index.js';
import { FormProps } from '@platform-ui-common/types/index.js';
import { AddressFields } from '@platform-ui/application/components/index.js';
import EINInput from './EINInput.js';
import Panel from './Panel.js';
import { BUSINESS_TAX_CLASSIFICATIONS } from './constants.js';

type LLCTaxClassificationItem = { label: string; value: W9LLCTaxClassifications };

const llcTaxClassificationItems: Array<LLCTaxClassificationItem> = [
  { label: 'C', value: W9LLCTaxClassifications.C },
  { label: 'S', value: W9LLCTaxClassifications.S },
  { label: 'P', value: W9LLCTaxClassifications.P },
];

const BusinessTaxFormClassification = ({
  formData,
  dispatch,
  errors,
}: FormProps<W9FormData>): JSX.Element => {
  const exemptPayeeCodeDropdownItems = Array.from({ length: 13 }, (_, i) => i + 1).map((i) => ({
    label: `${i}`,
    value: i,
  }));
  const exemptFATCACodeDropdownItems = Array.from({ length: 13 }, (_, i) => i + 65).map((i) => ({
    label: String.fromCharCode(i),
    value: String.fromCharCode(i),
  }));

  return (
    <>
      <Panel>
        <Typography variant="body">Business Tax Classification:*</Typography>
        <RadioGroup
          selectedValue={formData.businessTaxClassification}
          items={BUSINESS_TAX_CLASSIFICATIONS}
          name="businessTaxClassification"
          onChange={(value: string): void => {
            dispatch(
              fieldUpdate('businessTaxClassification', value as W9BusinessTaxClassifications),
            );
            dispatch(fieldUpdate('llcBusinessTaxClassification', undefined));
            dispatch(fieldUpdate('otherEntityType', undefined));
          }}
          error={!!errors.businessTaxClassification}
        />
        {formData.businessTaxClassification === BUSINESS_TAX_CLASSIFICATIONS[5].value && (
          <>
            <Typography variant="body">
              For Limited liability companies (LLC), please select the relevant tax classification:
            </Typography>
            <Dropdown
              id="llc-business-tax-classification"
              label="Tax Classification"
              value={formData.llcBusinessTaxClassification}
              onChange={(value): void =>
                dispatch(
                  fieldUpdate('llcBusinessTaxClassification', value as W9LLCTaxClassifications),
                )
              }
              items={llcTaxClassificationItems}
              placeholder="Tax Classification"
              error={errors.llcBusinessTaxClassification as string}
            />
          </>
        )}
        {formData.businessTaxClassification === BUSINESS_TAX_CLASSIFICATIONS[6].value && (
          <TextInput
            id="other-entity-type"
            label="Enter other entity type"
            value={formData.otherEntityType ?? ''}
            onChange={(value: string): void => dispatch(fieldUpdate('otherEntityType', value))}
            error={errors.otherEntityType as string}
          />
        )}
      </Panel>
      <Panel>
        <Typography variant="body">
          Please complete this section as shown on your income tax return:
        </Typography>
        <TextInput
          id="business-name"
          required
          label="Business Name"
          value={formData.name ?? ''}
          onChange={(value: string): void => dispatch(fieldUpdate('name', value))}
          error={errors.name as string}
        />
        <TextInput
          id="dba"
          label="DBA"
          value={formData.dba ?? ''}
          onChange={(value: string): void => dispatch(fieldUpdate('dba', value))}
          error={errors.dba as string}
        />
        <AddressFields
          id="w9-street-address"
          dispatch={dispatch}
          errors={errors}
          formData={formData}
        />
        <EINInput formData={formData} dispatch={dispatch} errors={errors} />
        <Typography variant="body">
          Exemptions
          <br />
          Codes apply only to certain entities
        </Typography>
        <Dropdown
          id="exemptPayeeCode"
          label="Exempt Payee Code (if any)"
          value={formData.exemptPayeeCode}
          onChange={(value: number): void => dispatch(fieldUpdate('exemptPayeeCode', value))}
          items={exemptPayeeCodeDropdownItems}
          placeholder="Exempt Payee Code (if any)"
          error={errors.exemptPayeeCode as string}
        />
        <Dropdown
          id="exemptFATCACode"
          label="Exemption from FATCA Reporting Code (if any)"
          value={formData.exemptFATCACode}
          onChange={(value: string): void => dispatch(fieldUpdate('exemptFATCACode', value))}
          items={exemptFATCACodeDropdownItems}
          placeholder="Exemption from FATCA Reporting Code (if any)"
          error={errors.exemptFATCACode as string}
        />
      </Panel>
    </>
  );
};

export default BusinessTaxFormClassification;
